import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { isChairmanReadOnly } from '../utils/access';

const API         = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/fines';
const MEMBERS_API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/members';

const BASE_FINE_TYPES = {
  meeting_absence: 'Meeting Absence',
  late_coming:     'Late Coming',
  burial_absence:  'Burial Absence',
  rosca:           'ROSCA Fine',
  manual:          'Manual Fine',
  mifl_penalty:    'MIFL Penalty (5%)',
};

const FINE_TYPE_COLORS = {
  meeting_absence: '#e67e22',
  late_coming:     '#f39c12',
  burial_absence:  '#c0392b',
  rosca:           '#8e44ad',
  manual:          '#2c3e50',
  mifl_penalty:    '#c0392b',
};

const DEFAULT_COLOR  = '#16a085';
const ACTIVITY_TYPES = ['meeting', 'burial', 'work_party', 'special_event'];

const PAYMENT_MODES = [
  { value: '', label: '-- Mode of Payment --' },
  { value: 'cash',      label: 'Cash' },
  { value: 'centenary', label: 'Centenary Bank' },
  { value: 'dfcu',      label: 'DFCU Bank' },
];

// ── PDF HELPERS ───────────────────────────────────────────
const addLetterhead = (doc, title, ref, date) => {
  doc.setFillColor(44, 62, 80); doc.rect(0, 0, 210, 28, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(18); doc.setFont('helvetica', 'bold');
  doc.text('KIGEZI ASSOCIATION', 105, 12, { align: 'center' });
  doc.setFontSize(11); doc.setFont('helvetica', 'normal');
  doc.text(title, 105, 21, { align: 'center' });
  doc.setTextColor(0, 0, 0); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text(`Ref: ${ref}`, 15, 36);
  doc.text(`Date: ${date}`, 195, 36, { align: 'right' });
  doc.setDrawColor(44, 62, 80); doc.setLineWidth(0.5); doc.line(15, 39, 195, 39);
  return 45;
};

const addMemberBlock = (doc, y, f) => {
  doc.setFillColor(240, 240, 240); doc.rect(15, y, 180, 22, 'F');
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
  doc.text('MEMBER DETAILS', 18, y + 6);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
  doc.text(`Name: ${f.full_name || f.member_name}`, 18, y + 13);
  doc.text(`Member No: ${f.member_number || '—'}`, 18, y + 19);
  doc.text(`Phone: ${f.phone || 'N/A'}`, 110, y + 13);
  doc.text(`Email: ${f.email || 'N/A'}`, 110, y + 19);
  return y + 28;
};

const addFooter = (doc) => {
  const ph = doc.internal.pageSize.height;
  doc.setFillColor(44, 62, 80); doc.rect(0, ph - 15, 210, 15, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  doc.text('Kigezi Association — Official Document — Confidential', 105, ph - 6, { align: 'center' });
};

const addSignatureBlock = (doc, y, labels, data, usePaymentSig = false) => {
  const colWidth = 170 / labels.length;
  const sig     = usePaymentSig ? data.payment_signature_snapshot : data.signature_snapshot;
  const officer = usePaymentSig ? data.payment_officer_name       : data.officer_name;
  const title   = usePaymentSig ? data.payment_officer_title      : data.officer_title;
  const dateStr = usePaymentSig
    ? (data.paid_at?.slice(0, 10) || data.date_issued?.slice(0, 10) || '')
    : (data.date_issued?.slice(0, 10) || '');
  if (sig) {
    try {
      const fmt = sig.includes('data:image/png') ? 'PNG' : 'JPEG';
      doc.setFillColor(255, 255, 255); doc.rect(15, y - 12, 42, 11, 'F');
      doc.addImage(sig, fmt, 15, y - 12, 40, 10);
    } catch (e) {}
  }
  doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
  labels.forEach((label, i) => {
    const x = 15 + (i * colWidth);
    doc.setDrawColor(100, 100, 100); doc.line(x, y, x + colWidth - 5, y);
    doc.text(label, x, y + 5);
    if (i === 0) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
      doc.text(officer || 'Finance Committee', x, y + 11);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
      doc.text('For Kigezi Association', x, y + 16);
      doc.setTextColor(0, 0, 0); doc.setFontSize(9);
    } else if (i === 1) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
      doc.text(title || 'Finance Committee', x, y + 11);
      doc.text(`Date: ${dateStr}`, x, y + 16);
      doc.setFontSize(9);
    } else {
      doc.text('Date: ___________', x, y + 11);
    }
  });
  return y + 22;
};

const generateFineMDN = (f, fineTypeLabels) => {
  const doc       = new jsPDF();
  const issueDate = f.date_issued?.slice(0, 10) || '';
  const dueDate   = f.due_date?.slice(0, 10) || 'Not set';
  const ref       = `FMD-${f.member_number}-${f.id}`;
  let y = addLetterhead(doc, 'FINE DEBIT NOTICE', ref, issueDate);
  y = addMemberBlock(doc, y, f);
  doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(231, 76, 60);
  doc.text('NOTICE OF FINE IMPOSITION', 105, y + 6, { align: 'center' });
  doc.setTextColor(0, 0, 0); y += 14;
  autoTable(doc, {
    startY: y,
    head: [['Field', 'Details']],
    body: [
      ['Fine Type', fineTypeLabels[f.fine_type] || f.fine_type],
      ['Reason / Description', f.reason],
      ['Date of Fine', issueDate],
      ['Payment Due Date', dueDate],
      ['Fine Amount (UGX)', Number(f.original_amount || f.amount || 0).toLocaleString()],
      ['Amount Paid (UGX)', Number(f.amount_paid || 0).toLocaleString()],
      ['Balance Outstanding (UGX)', Number(f.balance || 0).toLocaleString()],
      ['Status', (f.status || '').toUpperCase()],
    ],
    styles: { fontSize: 10 }, headStyles: { fillColor: [44, 62, 80] },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } }, margin: { left: 15, right: 15 },
  });
  y = doc.lastAutoTable.finalY + 10;
  doc.setFillColor(231, 76, 60); doc.rect(15, y, 180, 14, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text(`AMOUNT DUE: UGX ${Number(f.balance || 0).toLocaleString()} — PAY BY: ${dueDate}`, 105, y + 9, { align: 'center' });
  doc.setTextColor(0, 0, 0); y += 22;
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('This fine has been imposed in accordance with the Kigezi Association Constitution.', 15, y, { maxWidth: 180 }); y += 8;
  doc.text('Non-payment by the due date will result in automatic escalation to the Member Individual Financial Ledger (MIFL).', 15, y, { maxWidth: 180 }); y += 18;
  y = addSignatureBlock(doc, y, ['Issued By (Finance Committee)', 'Position'], f, false);
  addFooter(doc);
  doc.save(`FineNotice-${f.member_number}-${issueDate}.pdf`);
};

const generateFineReceipt = (f, fineTypeLabels) => {
  const doc      = new jsPDF();
  const paidDate = f.paid_at?.slice(0, 10) || f.date_issued?.slice(0, 10) || '';
  const ref      = `FRC-${f.member_number}-${f.id}`;
  let y = addLetterhead(doc, 'OFFICIAL RECEIPT — FINE PAYMENT', ref, paidDate);
  y = addMemberBlock(doc, y, f);
  autoTable(doc, {
    startY: y,
    head: [['Field', 'Details']],
    body: [
      ['Fine Type', fineTypeLabels[f.fine_type] || f.fine_type],
      ['Original Fine Amount (UGX)', Number(f.original_amount || f.amount || 0).toLocaleString()],
      ['Amount Paid (UGX)', Number(f.amount_paid || 0).toLocaleString()],
      ['Outstanding Balance After Payment (UGX)', Number(f.balance || 0).toLocaleString()],
      ['Payment Status', (f.status || '').toUpperCase()],
      ['Reason', f.reason],
    ],
    styles: { fontSize: 10 }, headStyles: { fillColor: [44, 62, 80] },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } }, margin: { left: 15, right: 15 },
  });
  y = doc.lastAutoTable.finalY + 10;
  const isFullyPaid = f.status === 'paid' || parseFloat(f.balance || 0) === 0;
  doc.setFillColor(isFullyPaid ? 39 : 243, isFullyPaid ? 174 : 156, isFullyPaid ? 96 : 18);
  doc.rect(15, y, 180, 14, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text(isFullyPaid ? 'FINE FULLY SETTLED — THANK YOU' : 'PARTIAL PAYMENT RECEIVED — BALANCE REMAINS', 105, y + 9, { align: 'center' });
  doc.setTextColor(0, 0, 0); y += 22;
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('This receipt confirms the fine payment recorded above. Please retain for your records.', 15, y, { maxWidth: 180 });
  y += 18;
  y = addSignatureBlock(doc, y, ['Received By (Finance Committee)', 'Position'], f, true);
  addFooter(doc);
  doc.save(`FineReceipt-${f.member_number}-${paidDate}.pdf`);
};

// ── COMPONENT ─────────────────────────────────────────────
function Fines() {
  const [tab, setTab]                           = useState('register');
  const [fines, setFines]                       = useState([]);
  const [activities, setActivities]             = useState([]);
  const [rates, setRates]                       = useState([]);
  const [members, setMembers]                   = useState([]);
  const [attendance, setAttendance]             = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [showFineForm, setShowFineForm]         = useState(false);
  const [editingId, setEditingId]               = useState(null);
  const [editData, setEditData]                 = useState({});
  const readOnly = isChairmanReadOnly();
  const isAdmin  = (() => { try { return JSON.parse(atob((localStorage.getItem('token') || '').split('.')[1])).role === 'admin'; } catch { return false; } })();
  const [error, setError]                       = useState('');
  const [success, setSuccess]                   = useState('');

  // Inline pay panel
  const [payingId, setPayingId]   = useState(null);
  const [payData, setPayData]     = useState({ amount_paid: '', mode_of_payment: '', receipt_ref: '' });

  // Escalate to MIFL confirmation
  const [escalateMiflId, setEscalateMiflId]   = useState(null);
  const [escalatingMifl, setEscalatingMifl]   = useState(false);

  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [newCategoryForm, setNewCategoryForm]         = useState({ fine_type: '', display_name: '', amount: '', description: '' });

  const [activityForm, setActivityForm] = useState({
    activity_type: 'meeting', title: '', activity_date: '', location: '', description: ''
  });

  const [fineForm, setFineForm] = useState({
    member_id: '', amount: '', reason: '', date_issued: '', fine_type: 'manual', due_date: ''
  });

  const [rateForm, setRateForm] = useState({ fine_type: 'meeting_absence', amount: '', description: '' });

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchFines(); fetchActivities(); fetchRates();
    axios.get(MEMBERS_API, { headers }).then(r => setMembers(r.data)).catch(() => {});
  }, []);

  const fineTypeLabels = {
    ...BASE_FINE_TYPES,
    ...Object.fromEntries(
      rates
        .filter(r => !BASE_FINE_TYPES[r.fine_type])
        .map(r => [r.fine_type, r.display_name || r.fine_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())])
    )
  };

  const allFineTypes = Object.entries(fineTypeLabels);

  const fetchFines      = () => { axios.get(API, { headers }).then(r => setFines(r.data)).catch(() => {}); };
  const fetchActivities = () => { axios.get(`${API}/activities`, { headers }).then(r => setActivities(r.data)).catch(() => {}); };
  const fetchRates      = () => { axios.get(`${API}/rates`, { headers }).then(r => setRates(r.data)).catch(() => {}); };

  const loadAttendance = (activity) => {
    setSelectedActivity(activity);
    axios.get(`${API}/activities/${activity.id}/attendance`, { headers }).then(r => setAttendance(r.data)).catch(() => {});
  };

  const updateAttendance = (member_id, status) => {
    axios.patch(`${API}/activities/${selectedActivity.id}/attendance/${member_id}`, { status }, { headers })
      .then(() => loadAttendance(selectedActivity))
      .catch(e => setError(e.response?.data?.error || 'Failed to update'));
  };

  const createActivity = async (e) => {
    e.preventDefault(); setError(''); setSuccess('');
    try {
      await axios.post(`${API}/activities`, activityForm, { headers });
      setSuccess('Activity created — record attendance then generate fines');
      setShowActivityForm(false);
      setActivityForm({ activity_type: 'meeting', title: '', activity_date: '', location: '', description: '' });
      fetchActivities();
    } catch (err) { setError(err.response?.data?.error || 'Failed to create activity'); }
  };

  const generateFinesForActivity = async (activity) => {
    setError(''); setSuccess('');
    try {
      const res = await axios.post(`${API}/activities/${activity.id}/generate-fines`, {}, { headers });
      setSuccess(res.data.message);
      fetchActivities(); fetchFines(); setSelectedActivity(null);
    } catch (err) { setError(err.response?.data?.error || 'Failed to generate fines'); }
  };

  const addManualFine = async (e) => {
    e.preventDefault(); setError(''); setSuccess('');
    try {
      await axios.post(API, fineForm, { headers });
      setSuccess('Fine added');
      setShowFineForm(false);
      setFineForm({ member_id: '', amount: '', reason: '', date_issued: '', fine_type: 'manual', due_date: '' });
      fetchFines();
    } catch (err) { setError(err.response?.data?.error || 'Failed to add fine'); }
  };

  const confirmFine = async (id) => {
    try { await axios.post(`${API}/${id}/confirm`, {}, { headers }); setSuccess('Fine confirmed'); fetchFines(); }
    catch (err) { setError(err.response?.data?.error || 'Failed to confirm'); }
  };

  const deleteFine = async (f) => {
    if (!window.confirm(`Delete this fine?\n\n${f.member_name || 'Member'} — ${(f.reason || f.fine_type || '').toString().slice(0, 60)}\nUGX ${Number(f.amount || 0).toLocaleString()}\n\nThis cannot be undone.`)) return;
    setError(''); setSuccess('');
    try { await axios.delete(`${API}/${f.id}`, { headers }); setSuccess('Fine deleted'); fetchFines(); }
    catch (err) { setError(err.response?.data?.error || 'Failed to delete fine'); }
  };

  const openPay = (f) => {
    setPayingId(f.id);
    setPayData({ amount_paid: f.balance, mode_of_payment: '', receipt_ref: '' });
    setEditingId(null); setEscalateMiflId(null);
  };

  const submitPay = async (id) => {
    if (!payData.amount_paid) return setError('Enter amount');
    setError('');
    try {
      await axios.post(`${API}/${id}/pay`, {
        amount_paid:     payData.amount_paid,
        mode_of_payment: payData.mode_of_payment || undefined,
        receipt_ref:     payData.receipt_ref     || undefined,
      }, { headers });
      setSuccess('Fine payment recorded');
      setPayingId(null); fetchFines();
    } catch (err) { setError(err.response?.data?.error || 'Failed to record payment'); }
  };

  const startEdit = (f) => {
    setEditingId(f.id);
    setEditData({ amount: f.amount, reason: f.reason, status: f.status, fine_type: f.fine_type, date_issued: f.date_issued?.slice(0, 10), due_date: f.due_date?.slice(0, 10) || '' });
    setPayingId(null); setEscalateMiflId(null);
  };

  const saveEdit = async (id) => {
    try { await axios.patch(`${API}/${id}`, editData, { headers }); setSuccess('Fine updated'); setEditingId(null); fetchFines(); }
    catch (err) { setError(err.response?.data?.error || 'Failed to update'); }
  };

  // ── Escalate to MIFL ──────────────────────────────────
  const confirmEscalateMifl = (f) => {
    setEscalateMiflId(f.id); setPayingId(null); setEditingId(null);
  };

  const submitEscalateMifl = async (id) => {
    setEscalatingMifl(true);
    try {
      const res = await axios.post(`${API}/${id}/escalate-to-mifl`, {}, { headers });
      setSuccess(res.data.message);
      setEscalateMiflId(null); fetchFines();
    } catch (err) { setError(err.response?.data?.error || 'Failed to escalate'); }
    finally { setEscalatingMifl(false); }
  };

  const escalate   = async (id) => {
    try { const res = await axios.post(`${API}/${id}/escalate`, {}, { headers }); setSuccess(res.data.message); fetchFines(); }
    catch (err) { setError(err.response?.data?.error || 'Cannot escalate yet'); }
  };

  const applyTier2 = async (id) => {
    try { const res = await axios.post(`${API}/${id}/apply-tier2`, {}, { headers }); setSuccess(res.data.message); fetchFines(); }
    catch (err) { setError(err.response?.data?.error || 'Failed to apply Tier 2 charge'); }
  };

  const saveRate = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/rates`, rateForm, { headers }); setSuccess('Rate updated'); fetchRates(); }
    catch (err) { setError(err.response?.data?.error || 'Failed to update rate'); }
  };

  const saveNewCategory = async (e) => {
    e.preventDefault(); setError(''); setSuccess('');
    if (!newCategoryForm.fine_type || !newCategoryForm.amount) return setError('Fine type key and amount are required');
    const slug = newCategoryForm.fine_type.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (!slug) return setError('Invalid fine type key');
    try {
      await axios.post(`${API}/rates`, {
        fine_type:   slug,
        amount:      newCategoryForm.amount,
        description: newCategoryForm.display_name
          ? `${newCategoryForm.display_name}${newCategoryForm.description ? ' — ' + newCategoryForm.description : ''}`
          : newCategoryForm.description || null,
      }, { headers });
      setSuccess(`New category "${newCategoryForm.display_name || slug}" added successfully`);
      setShowNewCategoryForm(false);
      setNewCategoryForm({ fine_type: '', display_name: '', amount: '', description: '' });
      fetchRates();
    } catch (err) { setError(err.response?.data?.error || 'Failed to add category'); }
  };

  const getFineColor = (type) => FINE_TYPE_COLORS[type] || DEFAULT_COLOR;
  const statusColor  = (s)    => ({ paid: '#2ecc71', unpaid: '#e74c3c', partial: '#f39c12', confirmed: '#3498db' }[s] || '#95a5a6');
  const totalUnpaid    = fines.filter(f => f.status !== 'paid').length;
  const totalPaid      = fines.filter(f => f.status === 'paid').length;
  const totalBalance   = fines.reduce((s, f) => s + parseFloat(f.balance || 0), 0);
  const totalCollected = fines.reduce((s, f) => s + parseFloat(f.amount_paid || 0), 0);
  const overdueCount   = fines.filter(f => f.status !== 'paid' && f.due_date && new Date(f.due_date) < new Date() && !f.escalated_to_mifl).length;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const isOverdue   = (f) => f.due_date && new Date(f.due_date) < today && f.status !== 'paid';
  const isDueSoon   = (f) => {
    if (!f.due_date || f.status === 'paid') return false;
    const due  = new Date(f.due_date);
    const diff = Math.ceil((due - today) / 86400000);
    return diff >= 0 && diff <= 7;
  };

  const tierBadge = (f) => {
    if (f.fine_type !== 'rosca') return null;
    return <span style={{ marginLeft: '5px', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', background: f.tier === 2 ? '#8e44ad' : '#f39c12', color: 'white' }}>T{f.tier}</span>;
  };

  const escalatedBadge = (f) => {
    if (!f.escalated_to_mifl) return null;
    return <span style={{ marginLeft: '5px', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', background: '#c0392b', color: 'white' }}>→MIFL</span>;
  };

  return (
    <div style={{ padding: '20px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ color: '#2c3e50', margin: 0 }}>Fines Management</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          {!readOnly && tab === 'register'   && <button onClick={() => setShowFineForm(!showFineForm)} style={btnStyle('#2c3e50')}>{showFineForm ? 'Cancel' : '+ Manual Fine'}</button>}
          {!readOnly && tab === 'activities' && <button onClick={() => { setShowActivityForm(!showActivityForm); setSelectedActivity(null); }} style={btnStyle('#2c3e50')}>{showActivityForm ? 'Cancel' : '+ New Activity'}</button>}
        </div>
      </div>

      {error   && <div style={alertStyle('#e74c3c')}>{error}<span onClick={() => setError('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}
      {success && <div style={alertStyle('#2ecc71')}>{success}<span onClick={() => setSuccess('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}

      {/* Overdue warning banner */}
      {overdueCount > 0 && (
        <div style={{ background: '#c0392b', color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '15px', fontSize: '13px' }}>
          ⚠ {overdueCount} fine{overdueCount !== 1 ? 's' : ''} past due date and not yet escalated to MIFL — use the → MIFL button to escalate
        </div>
      )}

      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={kpiCard('#e74c3c')}><div style={kpiLabel}>Unpaid Fines</div><div style={kpiValue}>{totalUnpaid}</div></div>
        <div style={kpiCard('#2ecc71')}><div style={kpiLabel}>Paid Fines</div><div style={kpiValue}>{totalPaid}</div></div>
        <div style={kpiCard('#c0392b')}><div style={kpiLabel}>Overdue (→MIFL)</div><div style={kpiValue}>{overdueCount}</div></div>
        <div style={kpiCard('#8e44ad')}><div style={kpiLabel}>Total Balance Owed</div><div style={{ ...kpiValue, fontSize: '14px' }}>UGX {totalBalance.toLocaleString()}</div></div>
        <div style={kpiCard('#2c3e50')}><div style={kpiLabel}>Total Collected</div><div style={{ ...kpiValue, fontSize: '14px' }}>UGX {totalCollected.toLocaleString()}</div></div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #ecf0f1' }}>
        {[['register', 'Fines Register'], ['activities', 'Activities'], ['rates', 'Fine Rates']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{ padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', background: tab === key ? '#2c3e50' : 'transparent', color: tab === key ? 'white' : '#7f8c8d', borderRadius: '4px 4px 0 0' }}>{label}</button>
        ))}
      </div>

      {/* ── FINES REGISTER ── */}
      {tab === 'register' && (
        <div>
          {showFineForm && (
            <form onSubmit={addManualFine} style={formStyle}>
              <h3 style={{ margin: '0 0 15px', width: '100%', color: '#2c3e50' }}>Add Manual Fine / Penalty</h3>
              <div style={fieldStyle}><label style={labelStyle}>Member *</label>
                <select style={inputStyle} value={fineForm.member_id} onChange={e => setFineForm({ ...fineForm, member_id: e.target.value })} required>
                  <option value=''>-- Select --</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.full_name} ({m.member_number})</option>)}
                </select>
              </div>
              <div style={fieldStyle}><label style={labelStyle}>Fine / Penalty Type</label>
                <select style={inputStyle} value={fineForm.fine_type} onChange={e => setFineForm({ ...fineForm, fine_type: e.target.value })}>
                  {allFineTypes.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div style={fieldStyle}><label style={labelStyle}>Amount (UGX) *</label>
                <input type="number" style={inputStyle} value={fineForm.amount} onChange={e => setFineForm({ ...fineForm, amount: e.target.value })} required />
              </div>
              <div style={fieldStyle}><label style={labelStyle}>Reason *</label>
                <input style={inputStyle} value={fineForm.reason} onChange={e => setFineForm({ ...fineForm, reason: e.target.value })} required />
              </div>
              <div style={fieldStyle}><label style={labelStyle}>Date Issued</label>
                <input type="date" style={inputStyle} value={fineForm.date_issued} onChange={e => setFineForm({ ...fineForm, date_issued: e.target.value })} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Due Date</label>
                <input type="date" style={inputStyle} value={fineForm.due_date} onChange={e => setFineForm({ ...fineForm, due_date: e.target.value })} />
                <span style={{ fontSize: '11px', color: '#95a5a6' }}>If not paid by this date → escalate to MIFL</span>
              </div>
              <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
                <button type="submit" style={btnStyle('#2c3e50')}>Save Fine</button>
                <button type="button" onClick={() => setShowFineForm(false)} style={btnStyle('#95a5a6')}>Cancel</button>
              </div>
            </form>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr style={{ background: '#2c3e50', color: 'white' }}>
                  <th style={thStyle}>Member</th><th style={thStyle}>Type</th>
                  <th style={thStyle}>Original</th><th style={thStyle}>Balance</th>
                  <th style={thStyle}>Paid</th><th style={thStyle}>Status</th>
                  <th style={thStyle}>Issued</th><th style={thStyle}>Due Date</th>
                  <th style={thStyle}>Reason</th><th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {fines.length === 0 && <tr><td colSpan="10" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>No fines yet</td></tr>}
                {fines.map((f, i) => {

                  // EDITING ROW
                  if (editingId === f.id) return (
                    <tr key={f.id} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                      <td style={tdStyle}><strong>{f.full_name}</strong></td>
                      <td style={tdStyle}>
                        <select value={editData.fine_type} onChange={e => setEditData({ ...editData, fine_type: e.target.value })} style={inlineInput}>
                          {allFineTypes.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </td>
                      <td style={tdStyle}><input type="number" value={editData.amount} onChange={e => setEditData({ ...editData, amount: e.target.value })} style={inlineInput} /></td>
                      <td style={tdStyle}>{Math.max(0, parseFloat(editData.amount || 0) - parseFloat(f.amount_paid || 0)).toLocaleString()}</td>
                      <td style={tdStyle}>{Number(f.amount_paid || 0).toLocaleString()}</td>
                      <td style={tdStyle}>
                        <select value={editData.status} onChange={e => setEditData({ ...editData, status: e.target.value })} style={inlineInput}>
                          <option value="unpaid">Unpaid</option><option value="partial">Partial</option>
                          <option value="confirmed">Confirmed</option><option value="paid">Paid</option>
                        </select>
                      </td>
                      <td style={tdStyle}><input type="date" value={editData.date_issued} onChange={e => setEditData({ ...editData, date_issued: e.target.value })} style={inlineInput} /></td>
                      <td style={tdStyle}><input type="date" value={editData.due_date} onChange={e => setEditData({ ...editData, due_date: e.target.value })} style={inlineInput} /></td>
                      <td style={tdStyle}><input value={editData.reason} onChange={e => setEditData({ ...editData, reason: e.target.value })} style={inlineInput} /></td>
                      <td style={tdStyle}>
                        <button onClick={() => saveEdit(f.id)} style={{ ...smBtn('#2ecc71'), marginRight: '4px' }}>Save</button>
                        <button onClick={() => setEditingId(null)} style={smBtn('#95a5a6')}>Cancel</button>
                      </td>
                    </tr>
                  );

                  // INLINE PAY ROW
                  if (payingId === f.id) return (
                    <tr key={f.id} style={{ background: '#eafaf1', borderLeft: '4px solid #27ae60' }}>
                      <td style={tdStyle} colSpan={2}>
                        <strong style={{ color: '#1e8449' }}>Pay fine — {f.full_name}</strong>
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>{fineTypeLabels[f.fine_type] || f.fine_type}</div>
                      </td>
                      <td style={tdStyle}>
                        <label style={{ display: 'block', fontSize: '10px', color: '#7f8c8d', marginBottom: '2px' }}>Amount</label>
                        <input type="number" style={inlineInput} value={payData.amount_paid} onChange={e => setPayData({ ...payData, amount_paid: e.target.value })} />
                      </td>
                      <td style={tdStyle} colSpan={2}>
                        <label style={{ display: 'block', fontSize: '10px', color: '#7f8c8d', marginBottom: '2px' }}>Mode of Payment</label>
                        <select style={inlineInput} value={payData.mode_of_payment} onChange={e => setPayData({ ...payData, mode_of_payment: e.target.value })}>
                          {PAYMENT_MODES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                      </td>
                      <td style={tdStyle} colSpan={3}>
                        <label style={{ display: 'block', fontSize: '10px', color: '#7f8c8d', marginBottom: '2px' }}>Receipt Ref</label>
                        <input style={inlineInput} placeholder='Optional' value={payData.receipt_ref} onChange={e => setPayData({ ...payData, receipt_ref: e.target.value })} />
                      </td>
                      <td style={tdStyle}>
                        <button onClick={() => submitPay(f.id)} style={{ ...smBtn('#27ae60'), marginRight: '4px' }}>Record</button>
                        <button onClick={() => setPayingId(null)} style={smBtn('#95a5a6')}>Cancel</button>
                      </td>
                    </tr>
                  );

                  // ESCALATE TO MIFL CONFIRMATION ROW
                  if (escalateMiflId === f.id) return (
                    <tr key={f.id} style={{ background: '#fdf3f3', borderLeft: '4px solid #c0392b' }}>
                      <td style={tdStyle} colSpan={9}>
                        <strong style={{ color: '#c0392b' }}>Escalate to MIFL — {f.full_name}?</strong>
                        <span style={{ fontSize: '12px', color: '#666', marginLeft: '10px' }}>
                          UGX {Number(f.balance).toLocaleString()} will be posted as MDN in MIFL. Member status → In Arrears.
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <button onClick={() => submitEscalateMifl(f.id)} disabled={escalatingMifl} style={{ ...smBtn('#c0392b'), marginRight: '4px' }}>
                          {escalatingMifl ? 'Posting…' : 'Confirm → MIFL'}
                        </button>
                        <button onClick={() => setEscalateMiflId(null)} style={smBtn('#95a5a6')}>Cancel</button>
                      </td>
                    </tr>
                  );

                  // NORMAL ROW
                  const rowBg = isOverdue(f) ? '#fff5f5' : isDueSoon(f) ? '#fffde7' : i % 2 === 0 ? '#f9f9f9' : 'white';
                  const borderLeft = isOverdue(f) ? '3px solid #c0392b' : isDueSoon(f) ? '3px solid #f39c12' : 'none';

                  return (
                    <tr key={f.id} style={{ background: rowBg, borderLeft }}>
                      <td style={tdStyle}>
                        <strong>{f.full_name}</strong><br />
                        <span style={{ fontSize: '11px', color: '#95a5a6' }}>{f.member_number}</span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ background: getFineColor(f.fine_type), color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
                          {fineTypeLabels[f.fine_type] || f.fine_type}
                        </span>
                        {tierBadge(f)}
                        {escalatedBadge(f)}
                      </td>
                      <td style={tdStyle}>{Number(f.original_amount || f.amount).toLocaleString()}</td>
                      <td style={{ ...tdStyle, color: f.balance > 0 ? '#e74c3c' : '#2ecc71', fontWeight: 'bold' }}>{Number(f.balance || 0).toLocaleString()}</td>
                      <td style={tdStyle}>{Number(f.amount_paid || 0).toLocaleString()}</td>
                      <td style={tdStyle}>
                        <span style={{ background: statusColor(f.status), color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{f.status?.toUpperCase()}</span>
                      </td>
                      <td style={tdStyle}>{f.date_issued ? f.date_issued.slice(0, 10) : '—'}</td>
                      <td style={tdStyle}>
                        {f.due_date ? (
                          <span style={{ color: isOverdue(f) ? '#c0392b' : isDueSoon(f) ? '#e67e22' : '#2c3e50', fontWeight: isOverdue(f) ? 'bold' : 'normal', fontSize: '12px' }}>
                            {f.due_date.slice(0, 10)}
                            {isOverdue(f) && ' ⚠'}
                            {isDueSoon(f) && !isOverdue(f) && ' (soon)'}
                          </span>
                        ) : <span style={{ color: '#bbb', fontSize: '12px' }}>—</span>}
                      </td>
                      <td style={{ ...tdStyle, fontSize: '12px', maxWidth: '180px' }}>{f.reason}</td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          <button onClick={() => generateFineMDN(f, fineTypeLabels)}
                            style={{ background: getFineColor(f.fine_type), color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>
                            📄 Notice
                          </button>
                          {(f.status === 'paid' || f.status === 'partial') && parseFloat(f.amount_paid || 0) > 0 && (
                            <button onClick={() => generateFineReceipt(f, fineTypeLabels)}
                              style={{ background: '#2ecc71', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>
                              📄 Receipt
                            </button>
                          )}
                          {!readOnly && <button onClick={() => startEdit(f)} style={smBtn('#e67e22')}>Edit</button>}
                          {!readOnly && isAdmin && !f.escalated_to_mifl && parseFloat(f.amount_paid || 0) <= 0 && f.status !== 'paid' && <button onClick={() => deleteFine(f)} style={smBtn('#c0392b')}>Delete</button>}
                          {!readOnly && f.status === 'unpaid' && <button onClick={() => confirmFine(f.id)} style={smBtn('#3498db')}>Confirm</button>}
                          {!readOnly && f.status !== 'paid' && <button onClick={() => openPay(f)} style={smBtn('#27ae60')}>Pay</button>}
                          {!readOnly && f.fine_type === 'rosca' && f.tier === 1 && <button onClick={() => escalate(f.id)} style={smBtn('#8e44ad')}>→T2</button>}
                          {!readOnly && f.fine_type === 'rosca' && f.tier === 2 && f.status !== 'paid' && <button onClick={() => applyTier2(f.id)} style={smBtn('#8e44ad')}>+20%</button>}
                          {/* Escalate to MIFL — only if overdue, unpaid, not yet escalated */}
                          {!readOnly && isOverdue(f) && f.status !== 'paid' && !f.escalated_to_mifl && (
                            <button onClick={() => confirmEscalateMifl(f)}
                              style={{ background: '#c0392b', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>
                              → MIFL
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── ACTIVITIES TAB ── */}
      {tab === 'activities' && (
        <div>
          {showActivityForm && (
            <form onSubmit={createActivity} style={formStyle}>
              <h3 style={{ margin: '0 0 15px', width: '100%', color: '#2c3e50' }}>New Activity</h3>
              <div style={fieldStyle}><label style={labelStyle}>Activity Type *</label>
                <select style={inputStyle} value={activityForm.activity_type} onChange={e => setActivityForm({ ...activityForm, activity_type: e.target.value })}>
                  {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ').toUpperCase()}</option>)}
                </select>
              </div>
              <div style={fieldStyle}><label style={labelStyle}>Title *</label>
                <input style={inputStyle} value={activityForm.title} onChange={e => setActivityForm({ ...activityForm, title: e.target.value })} placeholder="e.g. July 2026 General Meeting" required />
              </div>
              <div style={fieldStyle}><label style={labelStyle}>Date *</label>
                <input type="date" style={inputStyle} value={activityForm.activity_date} onChange={e => setActivityForm({ ...activityForm, activity_date: e.target.value })} required />
              </div>
              <div style={fieldStyle}><label style={labelStyle}>Location</label>
                <input style={inputStyle} value={activityForm.location} onChange={e => setActivityForm({ ...activityForm, location: e.target.value })} />
              </div>
              <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
                <button type="submit" style={btnStyle('#2c3e50')}>Create Activity</button>
                <button type="button" onClick={() => setShowActivityForm(false)} style={btnStyle('#95a5a6')}>Cancel</button>
              </div>
            </form>
          )}

          {!selectedActivity ? (
            <div>
              {activities.length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: '#95a5a6' }}>No activities yet</div>}
              {activities.map((a) => (
                <div key={a.id} style={{ background: 'white', border: '1px solid #ecf0f1', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                        <strong style={{ fontSize: '15px', color: '#2c3e50' }}>{a.title}</strong>
                        <span style={{ background: '#3498db', color: 'white', padding: '1px 8px', borderRadius: '4px', fontSize: '11px' }}>{a.activity_type.replace('_', ' ').toUpperCase()}</span>
                        {a.fines_generated && <span style={{ background: '#2ecc71', color: 'white', padding: '1px 8px', borderRadius: '4px', fontSize: '11px' }}>FINES DONE</span>}
                      </div>
                      <div style={{ fontSize: '13px', color: '#7f8c8d' }}>{a.activity_date ? a.activity_date.slice(0, 10) : ''} {a.location ? `— ${a.location}` : ''}</div>
                      <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '4px' }}>Present: {a.present_count} | Late: {a.late_count} | Absent: {a.absent_count} | Excused: {a.excused_count}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => loadAttendance(a)} style={smBtn('#2c3e50')}>Record Attendance</button>
                      {!a.fines_generated && <button onClick={() => generateFinesForActivity(a)} style={smBtn('#e74c3c')}>Generate Fines</button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <h3 style={{ margin: 0, color: '#2c3e50' }}>Attendance — {selectedActivity.title}</h3>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#7f8c8d' }}>{selectedActivity.activity_date?.slice(0, 10)} | Mark each member then click Generate Fines</p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {!selectedActivity.fines_generated && <button onClick={() => generateFinesForActivity(selectedActivity)} style={btnStyle('#e74c3c')}>Generate Fines</button>}
                  <button onClick={() => setSelectedActivity(null)} style={btnStyle('#95a5a6')}>← Back</button>
                </div>
              </div>
              <table style={tableStyle}>
                <thead><tr style={{ background: '#2c3e50', color: 'white' }}><th style={thStyle}>#</th><th style={thStyle}>Member</th><th style={thStyle}>Status</th><th style={thStyle}>Fine Triggered</th></tr></thead>
                <tbody>
                  {attendance.map((att, i) => (
                    <tr key={att.id} style={{ background: att.status === 'present' ? '#f0fdf4' : att.status === 'late' ? '#fffde7' : att.status === 'excused' ? '#f0f4ff' : '#fff5f5' }}>
                      <td style={tdStyle}>{i + 1}</td>
                      <td style={tdStyle}><strong>{att.full_name}</strong><br /><span style={{ fontSize: '11px', color: '#95a5a6' }}>{att.member_number}</span></td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {['present', 'late', 'excused', 'absent'].map(s => (
                            <button key={s} onClick={() => updateAttendance(att.member_id, s)}
                              style={{ padding: '4px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: att.status === s ? 'bold' : 'normal', background: att.status === s ? (s === 'present' ? '#2ecc71' : s === 'late' ? '#f39c12' : s === 'excused' ? '#3498db' : '#e74c3c') : '#ecf0f1', color: att.status === s ? 'white' : '#7f8c8d' }}>
                              {s.charAt(0).toUpperCase() + s.slice(1)}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        {att.fine_id ? <span style={{ color: '#e74c3c', fontSize: '12px', fontWeight: 'bold' }}>⚠ Fine #{att.fine_id}</span>
                          : (att.status === 'absent' || att.status === 'late') ? <span style={{ color: '#f39c12', fontSize: '12px' }}>Will be fined</span>
                          : <span style={{ color: '#95a5a6', fontSize: '12px' }}>No fine</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── FINE RATES ── */}
      {tab === 'rates' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h3 style={{ color: '#2c3e50', margin: 0 }}>AGM Fine Rates</h3>
            <button onClick={() => setShowNewCategoryForm(!showNewCategoryForm)} style={btnStyle('#16a085')}>
              {showNewCategoryForm ? 'Cancel' : '+ Add New Category'}
            </button>
          </div>
          <p style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '20px' }}>
            Fixed fine/penalty amounts per type. ROSCA fines use Tier 1 = 10% and Tier 2 = 20% (system calculated). MIFL Penalty = 5% of outstanding balance (manually calculated and posted).
          </p>

          {showNewCategoryForm && (
            <form onSubmit={saveNewCategory} style={{ ...formStyle, border: '2px solid #16a085', background: '#f0faf8', marginBottom: '24px' }}>
              <h4 style={{ width: '100%', margin: '0 0 12px', color: '#16a085' }}>Add New Fine / Penalty Category</h4>
              <div style={fieldStyle}>
                <label style={labelStyle}>Display Name *</label>
                <input style={inputStyle} placeholder="e.g. MIFL Penalty" value={newCategoryForm.display_name} onChange={e => setNewCategoryForm({ ...newCategoryForm, display_name: e.target.value })} required />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Fine Type Key *</label>
                <input style={inputStyle} placeholder="e.g. mifl_penalty" value={newCategoryForm.fine_type} onChange={e => setNewCategoryForm({ ...newCategoryForm, fine_type: e.target.value })} required />
                <span style={{ fontSize: '11px', color: '#95a5a6' }}>Lowercase, underscores only</span>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Amount (UGX) *</label>
                <input type="number" style={inputStyle} placeholder="0 for variable amounts" value={newCategoryForm.amount} onChange={e => setNewCategoryForm({ ...newCategoryForm, amount: e.target.value })} required />
              </div>
              <div style={{ ...fieldStyle, minWidth: '240px' }}>
                <label style={labelStyle}>Description (optional)</label>
                <input style={inputStyle} placeholder="Brief description" value={newCategoryForm.description} onChange={e => setNewCategoryForm({ ...newCategoryForm, description: e.target.value })} />
              </div>
              <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
                <button type="submit" style={btnStyle('#16a085')}>Save New Category</button>
                <button type="button" onClick={() => setShowNewCategoryForm(false)} style={btnStyle('#95a5a6')}>Cancel</button>
              </div>
            </form>
          )}

          <table style={tableStyle}>
            <thead><tr style={{ background: '#2c3e50', color: 'white' }}>
              <th style={thStyle}>Fine / Penalty Type</th><th style={thStyle}>Amount (UGX)</th><th style={thStyle}>Description</th>
            </tr></thead>
            <tbody>
              {rates.map((r, i) => (
                <tr key={r.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                  <td style={tdStyle}><span style={{ background: getFineColor(r.fine_type), color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{fineTypeLabels[r.fine_type] || r.fine_type}</span></td>
                  <td style={tdStyle}><strong>{r.amount > 0 ? Number(r.amount).toLocaleString() : 'Variable'}</strong></td>
                  <td style={tdStyle}>{r.description}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3 style={{ color: '#2c3e50', marginTop: '30px' }}>Update a Rate</h3>
          <form onSubmit={saveRate} style={{ ...formStyle, maxWidth: '500px' }}>
            <div style={fieldStyle}><label style={labelStyle}>Fine Type</label>
              <select style={inputStyle} value={rateForm.fine_type} onChange={e => setRateForm({ ...rateForm, fine_type: e.target.value })}>
                {rates.map(r => <option key={r.fine_type} value={r.fine_type}>{fineTypeLabels[r.fine_type] || r.fine_type}</option>)}
              </select>
            </div>
            <div style={fieldStyle}><label style={labelStyle}>New Amount (UGX)</label>
              <input type="number" style={inputStyle} value={rateForm.amount} onChange={e => setRateForm({ ...rateForm, amount: e.target.value })} required />
            </div>
            <div style={fieldStyle}><label style={labelStyle}>Description</label>
              <input style={inputStyle} value={rateForm.description} onChange={e => setRateForm({ ...rateForm, description: e.target.value })} />
            </div>
            <button type="submit" style={btnStyle('#2c3e50')}>Update Rate</button>
          </form>
        </div>
      )}
    </div>
  );
}

const btnStyle    = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' });
const smBtn       = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' });
const formStyle   = { background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '15px', border: '1px solid #ecf0f1' };
const fieldStyle  = { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '180px' };
const labelStyle  = { fontSize: '12px', color: '#7f8c8d', fontWeight: 'bold' };
const inputStyle  = { padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' };
const inlineInput = { padding: '4px 6px', borderRadius: '4px', border: '1px solid #27ae60', fontSize: '12px', background: '#eafaf1', width: '100%' };
const tableStyle  = { width: '100%', borderCollapse: 'collapse', marginTop: '10px' };
const thStyle     = { padding: '10px', textAlign: 'left', fontSize: '13px' };
const tdStyle     = { padding: '10px', borderBottom: '1px solid #eee', fontSize: '13px' };
const alertStyle  = (color) => ({ background: color, color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '15px' });
const kpiCard     = (color) => ({ background: color, color: 'white', padding: '15px 20px', borderRadius: '8px', minWidth: '140px' });
const kpiLabel    = { fontSize: '11px', opacity: 0.85, marginBottom: '4px' };
const kpiValue    = { fontSize: '24px', fontWeight: 'bold' };

export default Fines;